if (!customElements.get('product-info')) {
  let productInfoPopstateRefCount = 0;
  let productInfoPopstateHandler = null;

  function normalizePickerProductPath(value) {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    try {
      const u = new URL(trimmed, window.location.origin);
      let p = u.pathname;
      if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
      return p;
    } catch {
      const pathOnly = trimmed.split('?')[0];
      return pathOnly.length > 1 && pathOnly.endsWith('/') ? pathOnly.slice(0, -1) : pathOnly;
    }
  }

  function findPickerInputForPath(root, pathname) {
    const norm = normalizePickerProductPath(pathname);
    if (!norm) return null;
    for (const input of root.querySelectorAll('input[data-product-url]')) {
      const pu = input.dataset.productUrl;
      if (pu && normalizePickerProductPath(pu) === norm) return input;
    }
    return null;
  }

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      storedVariantSelections = null;
      sizeVariantListener = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSizeVariantHandler();

        if (this.dataset.updateUrl !== 'false') {
          if (productInfoPopstateRefCount === 0) {
            productInfoPopstateHandler = () => {
              const el =
                document.querySelector('product-info[id^="MainProduct"]') ||
                document.querySelector('product-info[data-update-url="true"]') ||
                document.querySelector('product-info:not([data-update-url="false"])');
              if (!el) {
                window.location.reload();
                return;
              }
              el.syncFromHistory();
            };
            window.addEventListener('popstate', productInfoPopstateHandler);
          }
          productInfoPopstateRefCount++;
        }

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initSizeVariantHandler() {
        const sizeVariantElement = document.querySelector('select[name="sizes"]');
        if (!sizeVariantElement) return;

        const handleSizeChange = (event) => {
          // Create a synthetic event object that mimics the structure expected by handleOptionValueChange
          const syntheticEvent = {
            data: {
              event: event,
              target: sizeVariantElement,
              selectedOptionValues: [event.target.value]
            }
          };
          
          this.handleOptionValueChange(syntheticEvent);
        };

        sizeVariantElement.addEventListener('change', handleSizeChange);
        
        // Store the cleanup function
        this.sizeVariantListener = () => {
          sizeVariantElement.removeEventListener('change', handleSizeChange);
        };
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.sizeVariantListener?.();

        if (this.dataset.updateUrl !== 'false') {
          productInfoPopstateRefCount = Math.max(0, productInfoPopstateRefCount - 1);
          if (productInfoPopstateRefCount === 0 && productInfoPopstateHandler) {
            window.removeEventListener('popstate', productInfoPopstateHandler);
            productInfoPopstateHandler = null;
          }
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Store current variant selections before updating
        this.storeCurrentVariantSelections();

        let productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        // Check if this is a size variant selection
        // productUrl = document.querySelector('select[name="sizes"]')?.value ?? productUrl;
        
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      storeCurrentVariantSelections() {
        if (!this.variantSelectors) return;
        
        this.storedVariantSelections = {};
        
        // Store selected values from all variant selectors
        this.variantSelectors.querySelectorAll('select, input[type="radio"]').forEach((input) => {
          // Trim the name to remove whitespace and newlines
          const trimmedName = input.name ? input.name.trim() : '';
          
          if (input.tagName === 'SELECT') {
            if (input.selectedOptions.length > 0) {
              this.storedVariantSelections[trimmedName] = input.selectedOptions[0].value;
            }
          } else if (input.type === 'radio' && input.checked) {
            this.storedVariantSelections[trimmedName] = input.value;
          }
        });
      }

      restoreVariantSelections() {
        if (!this.storedVariantSelections || !this.variantSelectors) return;
        const variantSelects = document.querySelector('variant-selects');

        // Restore selected values to variant selectors
        variantSelects.querySelectorAll('select, input[type="radio"]').forEach((input) => {
          // Trim the name to match the stored key
          const trimmedName = input.name ? input.name.trim() : '';
          const storedValue = this.storedVariantSelections[trimmedName];
          
          if (storedValue) {
            if (input.tagName === 'SELECT') {
              // Find and select the option with the stored value
              const option = Array.from(input.options).find(opt => opt.value === storedValue);
              if (option) {
                input.value = storedValue;
                // Trigger change event to update the UI
                // input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else if (input.type === 'radio' && input.value === storedValue) {
              input.checked = true;
              // Trigger change event to update the UI
              // input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
        
        // Clear stored selections after restoration
        this.storedVariantSelections = null;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          // Reinitialize JudgeMe reviews after product swap
          if (typeof window.jdgm !== 'undefined') {
            this.refreshJudgemeReviews();
          }

          if(typeof yotpo !== 'undefined' && yotpo.initWidgets()) {
            yotpo.initWidgets();
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const scrollY = window.pageYOffset
        
        this.abortController?.abort();
        this.abortController = new AbortController();
        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            this.restoreVariantSelections();

            requestAnimationFrame(() => {
              window.scrollTo(0, scrollY);
            })
            
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          })
          .finally(() => {
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        // Preserve view parameter for A/B testing
        const currentUrlParams = new URLSearchParams(window.location.search);
        const viewParam = currentUrlParams.get('view');
        if (viewParam) {
          params.push(`view=${encodeURIComponent(viewParam)}`);
        }

        const urlHasParams = url.includes('?');
        return urlHasParams ? `${url}&${params.join('&')}` : `${url}?${params.join('&')}`;
      }

      buildPopstateRequestUrl(productPath, variantId, shouldFetchFullPage) {
        const params = [];
        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);
        if (variantId) params.push(`variant=${encodeURIComponent(variantId)}`);
        const viewParam = new URLSearchParams(window.location.search).get('view');
        if (viewParam) params.push(`view=${encodeURIComponent(viewParam)}`);
        if (!params.length) return productPath;
        const sep = productPath.includes('?') ? '&' : '?';
        return `${productPath}${sep}${params.join('&')}`;
      }

      syncFromHistory() {
        const pathname = window.location.pathname;
        if (!findPickerInputForPath(this, pathname)) {
          window.location.reload();
          return;
        }

        this.resetProductFormState();

        const productUrl = pathname;
        const variantId = new URLSearchParams(window.location.search).get('variant');
        const shouldSwapProduct =
          normalizePickerProductPath(this.dataset.url) !== normalizePickerProductPath(productUrl);
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.pendingRequestUrl = productUrl;
        const requestUrl = this.buildPopstateRequestUrl(productUrl, variantId, shouldFetchFullPage);
        const focusInput = findPickerInputForPath(this, pathname);
        const targetId = focusInput?.id || this.id || `ProductInfo-${this.sectionId}`;

        this.renderProductInfo({
          requestUrl,
          targetId,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Reinitialize JudgeMe reviews after variant change
          if (typeof window.jdgm !== 'undefined') {
            this.refreshJudgemeReviews();
          }

          if(typeof yotpo !== 'undefined' && yotpo.initWidgets()) {
            yotpo.initWidgets();
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;

        const currentUrlParams = new URLSearchParams(window.location.search);
        const viewParam = currentUrlParams.get('view');

        const resolved = new URL(url, window.location.origin);
        if (variantId) resolved.searchParams.set('variant', String(variantId));
        if (viewParam) resolved.searchParams.set('view', viewParam);
        const next = `${resolved.pathname}${resolved.search}`;
        const current = `${window.location.pathname}${window.location.search}`;
        if (next === current) return;

        window.history.pushState({}, '', next);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      refreshJudgemeReviews() {
        if (typeof jdgm === 'undefined') {
          console.log('Judge.me not loaded yet');
          return;
        }
        
        // Small delay to ensure DOM has fully updated after viewTransition
        setTimeout(() => {
          const widget = document.querySelector('#judgeme_product_reviews');
          
          if (!widget) {
            console.log('No Judge.me widget found');
            return;
          }
      
          // CRITICAL: Remove the done-setup class from the NEW widget
          widget.classList.remove('jdgm--done-setup');
          
          // Convert to jQuery object
          const $widget = jdgm.$(widget);
          
          // Re-initialize the widget completely
          // This is Judge.me's internal initialization that sets up ALL event handlers
          if (jdgm._addEmptyWidgetHeader) {
            jdgm._addEmptyWidgetHeader($widget);
          }
          
          if (jdgm._customizeReviewWidget) {
            jdgm._customizeReviewWidget($widget);
          }
          
          if (jdgm.setupMediaGallery) {
            jdgm.setupMediaGallery($widget);
          }
          
          if (jdgm._renderAndSetupReviewForm) {
            jdgm._renderAndSetupReviewForm($widget);
          }
          
          if (jdgm._setupQuestionsForm) {
            jdgm._setupQuestionsForm($widget);
          }
          
          // THIS IS THE KEY: Setup load reviews events (includes pagination, search, sort)
          if (jdgm._setupLoadReviewsEventsFor) {
            jdgm._setupLoadReviewsEventsFor($widget);
          }
          
          // Setup form submissions (Write Review, Ask Question)
          if (jdgm._setupFormsSubmit) {
            jdgm._setupFormsSubmit($widget);
          }
          
          // Mark as initialized
          widget.classList.add('jdgm--done-setup');
          
          // Customize the reviews content
          if (jdgm.customizeReviews) {
            jdgm.customizeReviews();
          }
          
          // Update badges
          if (jdgm.customizeBadges) {
            jdgm.customizeBadges();
          }
        
          // Show the widget
          const legacyWidget = document.querySelector('.jdgm-legacy-widget-content');
          if (legacyWidget) {
            legacyWidget.style.display = 'block';
          }
          
          console.log('✅ Judge.me reviews refreshed successfully');
        }, 300); // Small delay for DOM to settle
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
